import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Search, X} from 'lucide-react';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  showIcon?: boolean;
  showLabel?: boolean;
  label?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
}

interface NominatimAddress {
  house_number?: string;
  road?: string;
  neighbourhood?: string;
  suburb?: string;
  city?: string;
  town?: string;
  village?: string;
  hamlet?: string;
  county?: string;
  state?: string;
  postcode?: string;
  country?: string;
  country_code?: string;
}

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  importance: number;
  address?: NominatimAddress;
}

// Nominatim returns full state names ("Michigan"), but property-data APIs
// (RentCast, RealEstateAPI) match reliably on the standard 2-letter
// abbreviation, so this converts it as part of building a clean address.
const US_STATE_ABBREVIATIONS: Record<string, string> = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
  Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD",
  Massachusetts: "MA", Michigan: "MI", Minnesota: "MN", Mississippi: "MS", Missouri: "MO",
  Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND", Ohio: "OH",
  Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT",
  Virginia: "VA", Washington: "WA", "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY",
  "District of Columbia": "DC", "Puerto Rico": "PR",
};

/**
 * Nominatim's `display_name` reorders/includes fields (county, full state
 * name, country) in a way property-data APIs don't expect as a search
 * address — e.g. "Avenue, 15290, Dix-Toledo Highway, Southgate, Wayne
 * County, Michigan, 48195, United States" instead of "15290 Dix-Toledo
 * Highway, Southgate, MI 48195". That mismatch was silently degrading
 * RentCast/RealEstateAPI matches (falling back to sparse or no results).
 * This rebuilds a standard "street, city, ST zip" string from Nominatim's
 * structured `address` components instead, falling back to display_name
 * only if too little structured data came back to build one.
 */
export const formatUSAddress = (result: NominatimResult, typedHouseNumber?: string): string => {
  const addr = result.address;
  if (!addr) return result.display_name;

  // OSM often has the street but not the number; keep the one the user typed.
  const street = [addr.house_number || (addr.road ? typedHouseNumber : ""), addr.road]
    .filter(Boolean)
    .join(" ");
  const city = addr.city || addr.town || addr.village || addr.hamlet || addr.suburb || "";
  const stateFull = addr.state || "";
  const state = US_STATE_ABBREVIATIONS[stateFull] || stateFull;
  const zip = addr.postcode || "";

  if (!street || !city) return result.display_name;

  const cityStateZip = [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [street, cityStateZip].filter(Boolean).join(", ");
};

/* ───────────── Relevance filtering ─────────────
 * Nominatim's free-form search is fuzzy: for "5500 Grand Lake Dr, San Antonio"
 * it can return an unrelated "5500 CR 48, Yalaha, FL". The helpers below drop
 * suggestions that do not match the house number / street name / state the
 * user actually typed, so a wrong place can never replace the typed address.
 */
const norm = (v: string) =>
  v.toLowerCase().replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();

const NOISE_TOKENS = new Set([
  "st", "street", "ave", "avenue", "av", "dr", "drive", "rd", "road", "ln", "lane",
  "blvd", "boulevard", "ct", "court", "cir", "circle", "pl", "place", "way", "pkwy",
  "parkway", "hwy", "highway", "ter", "terrace", "trl", "trail", "sq", "square",
  "n", "s", "e", "w", "ne", "nw", "se", "sw", "north", "south", "east", "west",
  "northeast", "northwest", "southeast", "southwest",
]);

const STATE_NAMES = Object.keys(US_STATE_ABBREVIATIONS);

const parseTypedAddress = (query: string) => {
  const parts = query.split(",").map((p) => p.trim()).filter(Boolean);
  const first = parts[0] || "";
  const m = first.match(/^(\d+[a-z]?)\s+(.+)$/i);
  const houseNumber = m ? m[1] : "";
  const streetTokens = m
    ? norm(m[2]).split(" ").filter((t) => t && !NOISE_TOKENS.has(t))
    : [];

  // A state typed as "TX" / "Texas" anywhere after the street part.
  let state = "";
  for (const part of parts.slice(1)) {
    for (const token of [part, ...part.split(/\s+/)]) {
      const up = token.toUpperCase();
      if (Object.values(US_STATE_ABBREVIATIONS).includes(up)) state = up;
      const full = STATE_NAMES.find((n) => n.toLowerCase() === token.toLowerCase());
      if (full) state = US_STATE_ABBREVIATIONS[full];
    }
  }
  return { houseNumber, streetTokens, state };
};

export const matchesTyped = (result: NominatimResult, query: string): boolean => {
  const { houseNumber, streetTokens, state } = parseTypedAddress(query);
  const addr = result.address;
  if (!addr) return true;

  if (state) {
    const resultState = US_STATE_ABBREVIATIONS[addr.state || ""] || addr.state || "";
    if (resultState && resultState.toUpperCase() !== state) return false;
  }

  // Only judge street/number once the user typed "<number> <street…>".
  if (!houseNumber || streetTokens.length === 0) return true;

  if (addr.house_number && norm(addr.house_number) !== norm(houseNumber)) return false;

  const road = norm(addr.road || "");
  if (!road) return false;
  const roadTokens = road.split(" ");
  // every typed street word must be (the start of) a word in the result's road
  return streetTokens.every((t) => roadTokens.some((r) => r.startsWith(t)));
};

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Enter full address...",
  id = "address",
  required = false,
  disabled = false,
  className = "",
  showIcon = true,
  showLabel = true,
  label,
  onKeyDown,
  onBlur,
}) => {
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const requestIdRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const searchAddresses = useCallback(async (query: string) => {
    if (query.length < 3) {
      requestIdRef.current += 1;
      abortRef.current?.abort();
      setSuggestions([]);
      setShowSuggestions(false);
      setIsLoading(false);
      return;
    }

    // Only the newest request may update the list (prevents stale suggestions
    // for an earlier, shorter query replacing the current ones).
    const requestId = ++requestIdRef.current;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        new URLSearchParams({
          q: query,
          format: 'json',
          addressdetails: '1',
          limit: '5',
          countrycodes: 'us',
          dedupe: '1'
        }),
        { signal: controller.signal }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch addresses');
      }

      const data: NominatimResult[] = await response.json();
      if (requestId !== requestIdRef.current) return;
      const relevant = data.filter((r) => matchesTyped(r, query));
      setSuggestions(relevant);
      // keep the dropdown open (even with 0 matches) so "use as typed" is offered
      setShowSuggestions(true);
      setSelectedIndex(-1);
    } catch (error: any) {
      if (error?.name === 'AbortError' || requestId !== requestIdRef.current) return;
      console.error('Error fetching address suggestions:', error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      if (requestId === requestIdRef.current) setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);

    // Debounce the API call
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchAddresses(newValue);
    }, 300);
  };

  const handleSuggestionClick = (suggestion: NominatimResult) => {
    onChange(formatUSAddress(suggestion, parseTypedAddress(value).houseNumber));
    setShowSuggestions(false);
    setSuggestions([]);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      onKeyDown?.(e);
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        } else {
          setShowSuggestions(false);
          onKeyDown?.(e);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        onKeyDown?.(e);
    }
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding suggestions to allow for clicks
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 150);
    onBlur?.();
  };

  const handleClear = useCallback(
  (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    onChange("");
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);

    inputRef.current?.focus();
  },
  [onChange]
);



  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      abortRef.current?.abort();
    };
  }, []);

  return (
    <div className="space-y-2 relative">
      {showLabel && (
        <Label htmlFor={id} className="flex items-center gap-2">
          {label || "Address"} {required && '*'}
          <MapPin className="h-4 w-4 text-muted-foreground" />
        </Label>
      )}
    <div className="relative">
      <Input
        ref={inputRef}
        id={id}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        disabled={disabled}
        required={required}
        autoComplete="off"
        className={`pr-10 ${className}`}
      />

      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
        {isLoading && showIcon && (
          <Search className="h-4 w-4 text-muted-foreground animate-spin" />
        )}

        {!isLoading && value && !disabled ? (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            className="
              flex h-5 w-5 items-center justify-center
              rounded-full text-muted-foreground
              hover:bg-muted hover:text-foreground
              transition-colors
            "
            aria-label="Clear address"
            title="Clear address"
          >
            <X className="h-4 w-4" />
          </button>
        ) : (
          !isLoading &&
          showIcon && <MapPin className="h-4 w-4 text-muted-foreground" />
        )}
      </div>
    </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && value.trim().length >= 3 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.place_id}
              className={`px-3 py-2 cursor-pointer hover:bg-accent hover:text-accent-foreground ${
                index === selectedIndex ? 'bg-accent text-accent-foreground' : ''
              }`}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {formatUSAddress(suggestion, parseTypedAddress(value).houseNumber)}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {suggestion.display_name}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {!isLoading && suggestions.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No matching suggestions
            </div>
          )}

          {/* Always let the user keep exactly what they typed */}
          <div
            className="px-3 py-2 cursor-pointer border-t border-border text-sm hover:bg-accent hover:text-accent-foreground"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setShowSuggestions(false);
              setSuggestions([]);
              inputRef.current?.focus();
            }}
          >
            Use “<span className="font-medium">{value.trim()}</span>” as typed
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;